/**
 * EmberSkillEffect — Fire tree Ember Fork: toggle warm aura.
 *
 * Toggle on to create a 2-tile radius aura centered on the player. The zone
 * follows the player via `followEntity` so it tracks position each frame.
 *
 * While the aura is active:
 *   - Every 1000ms: each enemy inside takes 2 **direct fire damage**. This is
 *     aura damage, NOT the `burning` DoT status. Cauterize's "burning enemy
 *     dies" condition, Chain Ignition, and Scar bonuses do not apply.
 *   - Every 3000ms: the player heals 1% max HP per enemy currently inside.
 *     Three enemies in range = 3% every 3 seconds. If the Hearth passive
 *     for `stationaryTriggerMs` and `regenMultiplier` is unlocked, heal ticks are
 *     multiplied by `regenMultiplier` (3×) when the player stands still long enough.
 *
 * Toggle off to dismiss the aura. Medium-tier cooldown between state switches
 * prevents on/off spamming — the cooldown starts on deactivation, not on
 * activation, so the player can always turn the aura off.
 *
 * Breaks Hide on activation (the warm glow is visible).
 *
 * @see fire-spec.md § Ember (`ember`)
 * @see ADR-064 (attunement shared systems architecture)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Zone config
// ---------------------------------------------------------------------------

export interface EmberZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly sourceSkillId: string;
  readonly followEntity: string;
  readonly onEntityExit?: (entityId: string) => void;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface EmberDeps {
  /** Returns the player's current world position in pixels. */
  readonly getPlayerPosition: () => { x: number; y: number };
  /** Returns the player's entity ID (for zone follow-tracking). */
  readonly getPlayerEntityId: () => string;
  /** Returns entity IDs of enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];
  /** Deal direct damage to a target entity. */
  readonly dealDamage: (entityId: string, damage: number) => void;
  readonly player: {
    readonly maxHp: number;
    heal(amount: number): void;
  };
  /** Break stealth / hide state. */
  readonly breakHide: () => void;
  readonly zones: {
    create(config: EmberZoneConfig): string;
    remove(zoneId: string): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeId: string, key: string): number;
  };
  /** Returns how long the player has stood still (ms). Used by Hearth passive. */
  readonly getStationaryDurationMs?: () => number;
  /**
   * Apply a status effect to a specific enemy entity.
   * Source-tagged per ADR-066 for DoT coexistence.
   * Used by Cinder to apply residual burning on aura exit.
   */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number; intensity: number; sourceSkillId: string },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface EmberConfig {
  /** Cooldown between toggle switches in ms. Medium tier. */
  readonly cooldownMs: number;
  /** Aura radius in tiles. */
  readonly radiusTiles: number;
  /** Direct fire damage per enemy per damage tick. */
  readonly damagePerTick: number;
  /** Interval between damage ticks in ms. */
  readonly damageTickMs: number;
  /** Fraction of max HP healed per enemy per heal tick. */
  readonly healPercent: number;
  /** Interval between heal ticks in ms. */
  readonly healTickMs: number;
}

const DEFAULT_CONFIG: EmberConfig = {
  cooldownMs: 1500,       // medium cooldown tier
  radiusTiles: 2,         // spec: 2-tile radius
  damagePerTick: 2,       // spec: 2 damage per tick
  damageTickMs: 1000,     // spec: every 1000ms
  healPercent: 0.01,      // spec: 1% max HP per enemy
  healTickMs: 3000,       // spec: every 3 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class EmberSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'ember';
  readonly failLabel = 'FIZZLE';

  private readonly _deps: EmberDeps;
  private readonly _config: EmberConfig;

  private _zoneId: string | null = null;
  private _damageAccumulator = 0;
  private _healAccumulator = 0;
  private _activations = 0;
  private _totalDamageDealt = 0;
  private _totalHealAmount = 0;

  constructor(deps: EmberDeps, config: Partial<EmberConfig> = {}) {
    super();
    this._deps = deps;
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for scenario assertions) ---

  get activations(): number { return this._activations; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get totalHealAmount(): number { return this._totalHealAmount; }
  get zoneId(): string | null { return this._zoneId; }

  // --- Toggle lifecycle ---

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._deps.breakHide();

    const pos = this._deps.getPlayerPosition();
    const entityId = this._deps.getPlayerEntityId();

    // Resolve additive radius bonuses (Hearthside upgrade adds 0.75).
    const bonusRadius = this._deps.resolver.getAdditiveEffect('ember', 'auraRadiusTiles');
    const resolvedRadius = this._config.radiusTiles + bonusRadius;

    // Cinder upgrade: residual burning DoT when enemies exit the aura.
    const residualBurnMs = this._deps.resolver.getAdditiveEffect(
      'ember',
      'residualBurnDurationMs',
    );
    const exitCallback: ((exitEntityId: string) => void) | undefined =
      residualBurnMs > 0
        ? (exitEntityId: string) => {
            this._deps.applyEnemyStatus(exitEntityId, 'burning', {
              durationMs: residualBurnMs,
              intensity: this._config.damagePerTick,
              sourceSkillId: 'ember_linger',
            });
          }
        : undefined;

    this._zoneId = this._deps.zones.create({
      id: `ember-aura-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'ember_aura',
      position: pos,
      shape: 'circle',
      radiusTiles: resolvedRadius,
      sourceSkillId: 'ember',
      followEntity: entityId,
      onEntityExit: exitCallback,
    });

    this._isActive = true;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
    this._activations++;
    return true;
  }

  protected onDeactivate(): void {
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    this.cooldownRemaining = this._config.cooldownMs;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }

  protected updateWhileActive(delta: number): void {
    this._damageAccumulator += delta;
    this._healAccumulator += delta;

    // Resolve current aura radius (base + Hearthside bonus).
    const bonusRadius = this._deps.resolver.getAdditiveEffect('ember', 'auraRadiusTiles');
    const resolvedRadius = this._config.radiusTiles + bonusRadius;

    // Resolve per-enemy heal bonus (Hearthside upgrade adds 0.005).
    const bonusHeal = this._deps.resolver.getAdditiveEffect('ember', 'regenPerEnemy');
    let resolvedHealPercent = this._config.healPercent + bonusHeal;

    // Hearth passive: stationary regen multiplier.
    const triggerMs = this._deps.resolver.getTreePassiveEffect('fire', 'stationaryTriggerMs');
    if (triggerMs > 0) {
      const stationaryMs = this._deps.getStationaryDurationMs?.() ?? 0;
      if (stationaryMs >= triggerMs) {
        const regenMultiplier = this._deps.resolver.getTreePassiveEffect(
          'fire',
          'regenMultiplier',
        );
        if (regenMultiplier > 0) {
          resolvedHealPercent *= regenMultiplier;
        }
      }
    }

    // Damage tick — direct fire damage to each enemy in range.
    while (this._damageAccumulator >= this._config.damageTickMs) {
      this._damageAccumulator -= this._config.damageTickMs;
      this._applyDamageTick(resolvedRadius);
    }

    // Heal tick — heal player based on enemy count in range.
    while (this._healAccumulator >= this._config.healTickMs) {
      this._healAccumulator -= this._config.healTickMs;
      this._applyHealTick(resolvedRadius, resolvedHealPercent);
    }
  }

  // ---------------------------------------------------------------------------
  // Tick logic
  // ---------------------------------------------------------------------------

  private _applyDamageTick(radiusTiles: number): void {
    const pos = this._deps.getPlayerPosition();
    const enemies = this._deps.getEnemiesInRadius(pos.x, pos.y, radiusTiles);

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, this._config.damagePerTick);
      this._totalDamageDealt += this._config.damagePerTick;
    }
  }

  private _applyHealTick(radiusTiles: number, healPercent: number): void {
    const pos = this._deps.getPlayerPosition();
    const enemies = this._deps.getEnemiesInRadius(pos.x, pos.y, radiusTiles);
    const enemyCount = enemies.length;

    if (enemyCount > 0) {
      const healAmount = Math.round(
        this._deps.player.maxHp * healPercent * enemyCount,
      );
      if (healAmount > 0) {
        this._deps.player.heal(healAmount);
        this._totalHealAmount += healAmount;
      }
    }
  }

  // --- Cleanup ---

  override destroy(): void {
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    super.destroy();
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }
}
